import Button from '@amzn/meridian/button';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import React, { useCallback } from 'react';
import { ALL_VALUES, ClaimSearchValueMap, CreditMemoStatus, SearchableClaimFields } from '../../redux/types';
import { SearchField, useTranslation } from '../blocks';

export const SEARCH_BY_SELECT_TEST_ID = 'claimFilter.selectSearchBy';
export const SEARCH_FIELD_TEST_ID = 'claimFilter.searchField';
export const SELECT_CLAIM_STATUS_TEST_ID = 'claimFilter.selectClaimStatus';
export const SELECT_CREDIT_MEMO_STATUS_TEST_ID = 'claimFilter.selectCreditMemoStatus';
export const CLEAR_FILTER_BUTTON_TEST_ID = 'claimFilter.clearButton';
export const SEARCH_BUTTON_TEST_ID = 'claimFilter.searchButton';

export type FilterValues = {
    query?: string;
    searchBy: SearchableClaimFields;
    claimStatusFilter?: string;
    cmStatusFilter?: string;
    dateRange: string[];
};

type ClaimFilterBarProps = {
    filterValues: FilterValues;
    onSetDateRange: (dateRange: string[]) => void;
    onFilterChange: (field: keyof FilterValues, value: string) => void;
    onClearFilters: () => void;
    submitQuery: (searchValueMap: ClaimSearchValueMap) => void;
};

export const ClaimFilterBar = React.memo(
    ({ filterValues, onSetDateRange, onFilterChange, onClearFilters, submitQuery }: ClaimFilterBarProps) => {
        const { t } = useTranslation('claimsListPage');
        const { t: tCommon } = useTranslation('common');
        const { t: tClaimStatus } = useTranslation('claimStatus');

        const buildSearchValueMap = useCallback((): ClaimSearchValueMap => {
            const searchValueMap: ClaimSearchValueMap = {};

            if (filterValues.query) {
                switch (filterValues.searchBy) {
                    case SearchableClaimFields.oracleTransactionNumber:
                        searchValueMap.oracleTransactionNumber = [filterValues.query];
                        break;
                    case SearchableClaimFields.uploadedByUser:
                        searchValueMap.uploadedByUser = [filterValues.query];
                        break;
                    default:
                        searchValueMap.resellerClaimNumber = [filterValues.query];
                        break;
                }
            }

            if (filterValues.claimStatusFilter !== ALL_VALUES && filterValues.claimStatusFilter) {
                searchValueMap.creationStatus = [filterValues.claimStatusFilter];
            }

            if (filterValues.cmStatusFilter !== ALL_VALUES && filterValues.cmStatusFilter) {
                searchValueMap.creditMemoStatus = [filterValues.cmStatusFilter];
            }

            return searchValueMap;
        }, [filterValues]);

        const handleSubmit = useCallback(() => {
            const searchValueMap = buildSearchValueMap();
            submitQuery(searchValueMap);
        }, [buildSearchValueMap, submitQuery]);

        const getLabelForSearchBox = (field: SearchableClaimFields): string => {
            switch (field) {
                case SearchableClaimFields.uploadedByUser:
                    return 'createdByUser-columnTitle';
                case SearchableClaimFields.oracleTransactionNumber:
                    return 'creditMemoNumber-columnTitle';
                default:
                case SearchableClaimFields.resellerClaimNumber:
                    return 'claimNumber-columnTitle';
            }
        };

        return (
            <Row spacing={'small'}>
                <Select
                    onChange={(value) => onFilterChange('searchBy', value)}
                    placeholder={tCommon('search-by-text')}
                    size={'xlarge'}
                    data-testid={SEARCH_BY_SELECT_TEST_ID}
                >
                    <SelectOption
                        value={SearchableClaimFields.resellerClaimNumber}
                        label={t('claimNumber-columnTitle')}
                    />
                    <SelectOption
                        value={SearchableClaimFields.oracleTransactionNumber}
                        label={t('creditMemoNumber-columnTitle')}
                    />
                    <SelectOption value={SearchableClaimFields.uploadedByUser} label={t('createdByUser-columnTitle')} />
                </Select>
                <SearchField
                    value={filterValues.query || ''}
                    onChange={(value) => onFilterChange('query', value)}
                    label={t(getLabelForSearchBox(filterValues.searchBy))}
                    onSubmit={handleSubmit}
                    searchButton={false}
                    data-testid={SEARCH_FIELD_TEST_ID}
                />
                <Select
                    value={filterValues.claimStatusFilter}
                    onChange={(value) => onFilterChange('claimStatusFilter', value)}
                    label={'Claim Status'}
                    placeholder={''}
                    size={'xlarge'}
                    data-testid={SELECT_CLAIM_STATUS_TEST_ID}
                >
                    <SelectOption value={ALL_VALUES} label={tClaimStatus('claimStatus-all-statuses')} />
                    <SelectOption value={'Created'} label={tClaimStatus('claimStatus-value_CREATED')} />
                    <SelectOption value={'In Progress'} label={tClaimStatus('claimStatus-value_IN_PROGRESS')} />
                    <SelectOption value={'Errored'} label={tClaimStatus('claimStatus-value_ERRORED')} />
                </Select>
                <Select
                    value={filterValues.cmStatusFilter}
                    onChange={(value) => onFilterChange('cmStatusFilter', value)}
                    label={t('creditMemoStatus-columnTitle')}
                    size={'xlarge'}
                    data-testid={SELECT_CREDIT_MEMO_STATUS_TEST_ID}
                >
                    <SelectOption value={ALL_VALUES} label={tClaimStatus('claimStatus-all-statuses')} />
                    <SelectOption
                        value={CreditMemoStatus.NOT_APPLICABLE}
                        label={t('creditMemo', { context: CreditMemoStatus.NOT_APPLICABLE })}
                    />
                    <SelectOption
                        value={CreditMemoStatus.NOT_PROCESSED}
                        label={t('creditMemo', { context: CreditMemoStatus.NOT_PROCESSED })}
                    />
                    <SelectOption
                        value={CreditMemoStatus.PROCESSING}
                        label={t('creditMemo', { context: CreditMemoStatus.PROCESSING })}
                    />
                    <SelectOption
                        value={CreditMemoStatus.COMPLETED}
                        label={t('creditMemo', { context: CreditMemoStatus.COMPLETED })}
                    />
                </Select>
                <DateRangePicker
                    value={filterValues.dateRange}
                    onChange={onSetDateRange}
                    monthsInView={2}
                    clearButton={true}
                    size={'medium'}
                    allowSameStartAndEnd={true}
                    startLabel={t('dateRangePicker-startPlaceholder')}
                    endLabel={t('dateRangePicker-endPlaceHolder')}
                />

                <Button onClick={handleSubmit} type={'primary'} size={'large'} data-testid={SEARCH_BUTTON_TEST_ID}>
                    {tCommon('search-text')}
                </Button>
                <Button
                    onClick={onClearFilters}
                    type={'tertiary'}
                    size={'large'}
                    data-testid={CLEAR_FILTER_BUTTON_TEST_ID}
                >
                    {tCommon('clear-text')}
                </Button>
            </Row>
        );
    }
);
